import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { event, program, chefs, menu, gallery, faqs } from "@/data/eventData";
import { api } from "@/lib/api";

export const defaultContent = {
  event,
  program,
  chefs,
  menu,
  gallery,
  faqs,
};

export function mergeContent(saved = {}) {
  const savedEvent = saved.event || {};

  return {
    ...defaultContent,
    ...saved,
    event: {
      ...event,
      ...savedEvent,
      social: { ...event.social, ...(savedEvent.social || {}) },
      ticketButton: {
        ...event.ticketButton,
        ...(savedEvent.ticketButton || {}),
      },
      location: {
        ...event.location,
        ...(savedEvent.location || {}),
      },
    },
  };
}

const EventContentContext = createContext({
  content: defaultContent,
  loading: true,
  error: "",
  refreshContent: async () => {},
});

export function EventContentProvider({ children }) {
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [error, setError] = useState("");

  const refreshContent = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/content");
      setContent(mergeContent(response.data.content || {}));
    } catch (err) {
      setError("No se pudo cargar el contenido editable. Se muestran datos base.");
      setContent(defaultContent);
    } finally {
      setLoading(false);
      setInitialLoadComplete(true);
    }
  };

  useEffect(() => {
    refreshContent();
  }, []);

  const value = useMemo(
    () => ({
      content,
      loading,
      error,
      refreshContent,
      event: content.event || defaultContent.event,
      program: content.program || defaultContent.program,
      chefs: content.chefs || defaultContent.chefs,
      menu: content.menu || defaultContent.menu,
      gallery: content.gallery || defaultContent.gallery,
      faqs: content.faqs || defaultContent.faqs,
    }),
    [content, loading, error]
  );

  if (!initialLoadComplete) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#FAFAF7]"
        role="status"
        aria-label="Cargando sitio"
      >
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#D4A373] border-t-[#3B5110]" />
      </div>
    );
  }

  return (
    <EventContentContext.Provider value={value}>
      {children}
    </EventContentContext.Provider>
  );
}

export function useEventContent() {
  return useContext(EventContentContext);
}